import { chooseDBTranslation } from 'utils/translations'
import { Paragraph, TextRun } from 'docx'
import { CVData } from 'types/printInterfaces'
import { itemHeading, itemHeadingTabStops, itemParagraphSpacing } from './itemHeading'
import { fontSize } from './pageLayout'
import { convertToDisplayDate } from 'utils/utils'
import { IHideSetting } from 'types/layoutInterfaces'
import { isDetailHidden } from '../hideSettings'
import { i18n as i18nType, TFunction } from 'i18next'

/**
 * Generates the project section of a CV.
 *
 * @param {Object} i18n - the internationalization object
 * @param {Object} data - the CV data
 * @param {Object} t - the translation object
 * @param {IHideSetting | undefined} hideSetting - the hide setting for the CV
 * @return {Array} an array of Paragraph objects representing the project section
 * @notExported
 */
const project = (i18n: i18nType, data: CVData, t: TFunction, hideSetting: IHideSetting | undefined): Paragraph[] => {
  // return project section
  const projects: Paragraph[] = []

  for (let i = 0; i < data.projects.length; i++) {
    const project = data.projects[i]
    let date = ''
    if (project.startDate) {
      date += `${convertToDisplayDate(project.startDate, project.dateResolution)} - `
    }
    if (project.endDate) {
      date += `${convertToDisplayDate(project.endDate, project.dateResolution)}`
    }

    let roles = ''
    if (project.ProjectRoles) {
      project.ProjectRoles.forEach(role => {
        roles += chooseDBTranslation(i18n, role.Skill).name + '   '
      })
    }

    let skills = ''
    if (project.PersonSkills) {
      project.PersonSkills.forEach(skill => {
        skills += chooseDBTranslation(i18n, skill.Skill).name + '   '
      })
    }

    let industries = ''
    if (project.Industries) {
      project.Industries.forEach(industry => {
        industries += chooseDBTranslation(i18n, industry.Skill).name + '   '
      })
    }

    const primaryRole = chooseDBTranslation(i18n, project).primaryRole
      ? chooseDBTranslation(i18n, project).primaryRole
      : ''

    const children = [
      ...itemHeading(
        !isDetailHidden(hideSetting, 'name') && project ? chooseDBTranslation(i18n, project).name : '',
        !isDetailHidden(hideSetting, 'date') && project ? date : ''
      ),
      new TextRun({
        text: !isDetailHidden(hideSetting, 'primaryRole') && project ? `${t('print.project.role')}: ` : '',
        bold: true,
        break: 1,
      }),
      new TextRun({
        text: !isDetailHidden(hideSetting, 'primaryRole') && primaryRole ? primaryRole : '',
      }),
      ...(!isDetailHidden(hideSetting, 'projectRoles') && roles.length > 0
        ? [
            new TextRun({
              text: `${t('print.project.roles')}: `,
              bold: true,
              break: 1,
            }),
            new TextRun({
              text: roles,
            }),
          ]
        : []),
      new TextRun({
        text: !isDetailHidden(hideSetting, 'customerName') && project ? `${t('print.project.customer')}: ` : '',
        bold: true,
        break: 1,
      }),
      new TextRun({
        text:
          !isDetailHidden(hideSetting, 'customerName') && project
            ? chooseDBTranslation(i18n, project).customerName
            : '',
      }),
      new TextRun({
        text: !isDetailHidden(hideSetting, 'employerName') && project ? `${t('print.project.employer')}: ` : '',
        bold: true,
        break: 1,
      }),
      new TextRun({
        text:
          !isDetailHidden(hideSetting, 'employerName') && project
            ? chooseDBTranslation(i18n, project).employerName
            : '',
      }),
    ]

    if (!isDetailHidden(hideSetting, 'personSkills') && skills.length > 0) {
      children.push(
        new TextRun({
          text: `${t('print.project.skills')}: `,
          bold: true,
          break: 1,
        }),
        new TextRun({
          text: skills,
        })
      )
    }

    if (!isDetailHidden(hideSetting, 'industries') && industries.length > 0) {
      children.push(
        new TextRun({
          text: `${t('print.project.industries')}: `,
          bold: true,
          break: 1,
        }),
        new TextRun({
          text: industries,
        })
      )
    }

    // the description is the last row, like in the PDF
    children.push(
      new TextRun({
        text:
          !isDetailHidden(hideSetting, 'description') && project ? chooseDBTranslation(i18n, project).description : '',
        size: fontSize.small,
        break: 1,
      })
    )

    projects.push(
      new Paragraph({
        keepLines: true,
        style: 'CaleoNormal',
        tabStops: itemHeadingTabStops,
        spacing: itemParagraphSpacing,
        children: children,
      })
    )
  }

  return projects
}

export default project
